import { Eye } from 'lucide-react';

import Sidebar from '@/shared/components/Sidebar';
import TourGuide from '@/shared/components/TourGuide';
import { useAuth } from '@/shared/auth/AuthContext';

/**
 * Sortie du mode observation.
 *
 * Le jeton du super administrateur est mis de côté avant l'entrée : on le remet en
 * place plutôt que de déconnecter. Sans cela, quitter l'observation renverrait à
 * l'écran de connexion, et il faudrait se réauthentifier pour continuer à travailler.
 */
function quitterObservation() {
  const jetonInitial = localStorage.getItem('flexcore_jeton_superadmin');
  localStorage.removeItem('flexcore_jeton_superadmin');
  if (jetonInitial) {
    localStorage.setItem('flexcore_token', jetonInitial);
    window.location.href = '/superadmin';
  } else {
    localStorage.removeItem('flexcore_token');
    window.location.href = '/login';
  }
}

/**
 * Bandeau permanent pendant l'observation d'un sous-compte.
 *
 * Il n'est pas décoratif : le serveur refuse toute écriture tant que le jeton porte
 * la revendication d'observation, et quelqu'un qui l'ignorerait attribuerait ses
 * échecs à un bug. Le bandeau dit qui observe, pour que la trace soit lisible des
 * deux côtés.
 */
function BandeauObservation({ email, observePar }) {
  return (
    <div
      className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-[12px] font-medium text-black"
      data-testid="bandeau-observation"
    >
      <span className="flex items-center gap-2 min-w-0">
        <Eye className="h-4 w-4 shrink-0" />
        <span className="truncate">
          Mode observation — vous voyez les écrans de <strong>{email}</strong>.
          Toute modification est refusée.
          {observePar && <> Session ouverte par <strong>{observePar}</strong>.</>}
        </span>
      </span>
      <button
        onClick={quitterObservation}
        className="shrink-0 rounded bg-black px-3 py-1 text-[11px] font-semibold text-amber-500 hover:bg-neutral-800"
        data-testid="quitter-observation"
      >
        Quitter l&apos;observation
      </button>
    </div>
  );
}

// Layout principal des écrans authentifiés : sidebar + zone de contenu scrollable.
export default function AppLayout({ children }) {
  const { user } = useAuth();

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {user?.observation && (
        <BandeauObservation email={user.email} observePar={user.observe_par_email} />
      )}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-auto bg-[#FAFAFA]">
          {children}
        </main>
      </div>
      {/* Le tour du premier passage. Monté ICI et pas dans chaque écran : il doit
          pouvoir désigner la barre latérale, qui n'appartient à aucun écran.

          ⚠️ PAS PENDANT UNE OBSERVATION. Un super administrateur qui regarde le compte
          d'un client verrait un tour écrit pour ce client — et le marquerait comme vu
          DANS SON PROPRE navigateur, privant l'intéressé du sien. */}
      {user && !user.observation && (
        <TourGuide utilisateur={user.id || user.email} role={user.role} />
      )}
    </div>
  );
}
